import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { useSaveContent } from "./AdminLayout";
import { Card, Text, Area, Lines, ObjectList, btnPrimary, btnGhost } from "./fields";

const tabs = [
  { key: "voorwaarden", label: "Algemene voorwaarden" },
  { key: "garantie", label: "Garantievoorwaarden" },
];

export default function AdminVoorwaarden() {
  const content = useContent();
  const { save, saving } = useSaveContent();
  const [tab, setTab] = useState("voorwaarden");
  const [draft, setDraft] = useState(content[tab]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(content[tab]);
    setDirty(false);
  }, [tab, content]);

  const set = (key) => (v) => {
    setDraft((d) => ({ ...d, [key]: v }));
    setDirty(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    await save({ [tab]: draft });
  };

  if (!draft) return null;

  return (
    <div data-testid="admin-voorwaarden-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold uppercase tracking-wide text-[#111111]">Voorwaarden</h1>
        <p className="mt-1 text-sm text-[#555555]">
          Pas de algemene voorwaarden en garantievoorwaarden aan. In de algemene voorwaarden kun je de codes {"{naam}"}, {"{email}"}, {"{telefoon}"} en {"{plaats}"} gebruiken; die worden automatisch ingevuld.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-[#E2E0D9]">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`voorwaarden-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 font-display text-xs font-bold uppercase tracking-wide transition-colors ${
              tab === t.key ? "border-[#111111] text-[#111111]" : "border-transparent text-[#8E8D8A] hover:text-[#111111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card title="Versie">
          <Text label="Versie-aanduiding (onderaan de pagina)" value={draft.versie} onChange={set("versie")} testid="voorwaarden-versie" />
        </Card>

        {tab === "voorwaarden" ? (
          <Card title="Artikelen">
            <ObjectList
              label="Artikelen (worden automatisch genummerd)"
              value={draft.artikelen}
              onChange={set("artikelen")}
              testid="av-artikelen"
              empty={{ titel: "", items: [] }}
              fields={[
                { key: "titel", label: "Titel van het artikel" },
                { key: "items", label: "Leden (één per regel, worden genummerd als 1.1, 1.2 ...)", lines: true },
              ]}
            />
          </Card>
        ) : (
          <>
            <Card title="Garantietermijnen per onderdeel">
              <ObjectList
                label="Termijnen"
                value={draft.termijnen}
                onChange={set("termijnen")}
                testid="garantie-termijnen"
                empty={{ duur: "", titel: "", tekst: "" }}
                fields={[
                  { key: "duur", label: "Duur (bijv. 10 jaar)" },
                  { key: "titel", label: "Onderdeel" },
                  { key: "tekst", label: "Toelichting", multiline: true },
                ]}
              />
            </Card>
            <Card title="Dekking">
              <Lines label="Wat valt onder de garantie?" value={draft.dekking} onChange={set("dekking")} testid="garantie-dekking" />
              <Area label="Toelichting bij de dekking" value={draft.dekkingToelichting} onChange={set("dekkingToelichting")} rows={3} testid="garantie-dekking-toelichting" />
            </Card>
            <Card title="Uitsluitingen">
              <Lines label="Wat valt er niet onder?" value={draft.uitsluitingen} onChange={set("uitsluitingen")} testid="garantie-uitsluitingen" />
            </Card>
            <Card title="Voorwaarden">
              <Lines label="Wanneer geldt de garantie? (één voorwaarde per regel, genummerd)" value={draft.voorwaarden} onChange={set("voorwaarden")} testid="garantie-voorwaarden" />
            </Card>
            <Card title="Procedure">
              <ObjectList
                label="Stappen"
                value={draft.procedure}
                onChange={set("procedure")}
                testid="garantie-procedure"
                empty={{ titel: "", tekst: "" }}
                fields={[
                  { key: "titel", label: "Stap" },
                  { key: "tekst", label: "Uitleg", multiline: true },
                ]}
              />
            </Card>
          </>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" data-testid="voorwaarden-save" disabled={saving || !dirty} className={btnPrimary}>
            <Save className="h-4 w-4" /> {saving ? "Opslaan..." : "Opslaan"}
          </button>
          <button type="button" data-testid="voorwaarden-undo" disabled={!dirty} onClick={() => { setDraft(content[tab]); setDirty(false); }} className={btnGhost}>
            <RotateCcw className="h-4 w-4" /> Wijzigingen ongedaan maken
          </button>
          <a href={tab === "voorwaarden" ? "/algemene-voorwaarden" : "/garantie"} target="_blank" rel="noreferrer" className="text-xs text-[#8E8D8A] underline underline-offset-2">
            Bekijk de pagina op de site
          </a>
        </div>
      </form>
    </div>
  );
}
